import type {
  accountType_enum,
  activeStatus_enum,
  party_enum,
  bankAccountCategory_enum,
} from '@vrplatform/graphql';
import {
  useQueryState as useQueryStateNuqs,
  useQueryStates as useQueryStatesNuqs,
  parseAsString,
  type HistoryOptions,
  parseAsBoolean,
  parseAsInteger,
  parseAsArrayOf,
  parseAsIsoDate,
  type UseQueryStatesKeysMap,
  parseAsStringLiteral,
  createSerializer,
} from 'nuqs';

import { NuqsAdapter } from 'nuqs/adapters/react-router/v8';
import { NuqsTestingAdapter } from 'nuqs/adapters/testing';

type UseQueryStateArgs<T> = Parameters<typeof useQueryStateNuqs<T>>;
type UseQueryStatesArgs<TKeyMap extends UseQueryStatesKeysMap> = Parameters<
  typeof useQueryStatesNuqs<TKeyMap>
>;

function useQueryState<T>(
  key: UseQueryStateArgs<T>[0],
  options: UseQueryStateArgs<T>[1]
) {
  return useQueryStateNuqs(key, {
    ...options,
    history: options.history || 'push',
  });
}
function useQueryStates<TKeyMap extends UseQueryStatesKeysMap>(
  ...args: UseQueryStatesArgs<TKeyMap>
) {
  return useQueryStatesNuqs(args[0], {
    ...args[1],
    history: args[1]?.history || 'push',
  });
}

// Classification
const CLASSIFICATIONS = [
  'asset',
  'liability',
  'equity',
  'revenue',
  'costOfRevenue',
  'expense',
] as const;
const parseAsClassification = parseAsStringLiteral(CLASSIFICATIONS);

// Account Type
const ACCOUNT_TYPES = Object.keys({
  bank: 'Bank',
  ledger: 'Ledger',
  recurringFee: 'Fees & Commissions',
  nonPosting: 'Reference',
} satisfies Record<accountType_enum, string>) as accountType_enum[];
const parseAsAccountType = parseAsStringLiteral(ACCOUNT_TYPES);

// Status
const ACTIVE_STATUSES: activeStatus_enum[] = ['active', 'inactive'];
const parseAsActiveStatus = parseAsStringLiteral(ACTIVE_STATUSES);

// Party
const PARTIES: party_enum[] = ['owners', 'manager'];
const parseAsParty = parseAsStringLiteral(PARTIES);

// Bank Account Category
const BANK_ACCOUNT_CATEGORIES = Object.keys({
  external: 'External',
  operating: 'Operating',
  trust: 'Trust',
} satisfies Record<
  bankAccountCategory_enum,
  string
>) as bankAccountCategory_enum[];
const parseAsBankAccountCategory = parseAsStringLiteral(
  BANK_ACCOUNT_CATEGORIES
);

const BOOK = ['trust', 'operating'] as const;
const parseAsBook = parseAsStringLiteral(BOOK);

const LISTING_SCOPES = ['exact', 'withChildren'] as const;
const parseAsListingScope = parseAsStringLiteral(LISTING_SCOPES);

export {
  NuqsAdapter,
  createSerializer,
  NuqsTestingAdapter,
  useQueryState,
  useQueryStates,
  type HistoryOptions,
  parseAsString,
  parseAsBoolean,
  parseAsInteger,
  parseAsArrayOf,
  parseAsIsoDate,
  parseAsStringLiteral,
  parseAsClassification,
  parseAsActiveStatus,
  parseAsParty,
  parseAsAccountType,
  parseAsBankAccountCategory,
  parseAsBook,
  parseAsListingScope,
};
